import { useState, useEffect } from 'react';
import {
  adminUsersService,
  PlatformAdmin,
  CreateAdminUserDto,
  UpdateAdminUserDto,
  ChangePasswordDto,
  ResetPasswordDto,
  AdminStats,
} from '../../services/platform-admin/admin-users.service';
import { usePlatformAdminAuthStore } from '../../stores/platform-admin-auth.store';

export function useAdminUsers() {
  const [admins, setAdmins] = useState<PlatformAdmin[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const token = usePlatformAdminAuthStore((state) => state.token);

  const loadAdmins = async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);
      const [adminsData, statsData] = await Promise.all([
        adminUsersService.getAllAdmins(token),
        adminUsersService.getStats(token),
      ]);
      setAdmins(adminsData);
      setStats(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAdmins();
  }, [token]);

  const createAdmin = async (data: CreateAdminUserDto) => {
    if (!token) throw new Error('No token');
    await adminUsersService.createAdmin(token, data);
    await loadAdmins();
  };

  const updateAdmin = async (id: string, data: UpdateAdminUserDto) => {
    if (!token) throw new Error('No token');
    await adminUsersService.updateAdmin(token, id, data);
    await loadAdmins();
  };

  const deleteAdmin = async (id: string) => {
    if (!token) throw new Error('No token');
    await adminUsersService.deleteAdmin(token, id);
    await loadAdmins();
  };

  const changePassword = async (data: ChangePasswordDto) => {
    if (!token) throw new Error('No token');
    return adminUsersService.changePassword(token, data);
  };

  const resetPassword = async (id: string, data: ResetPasswordDto) => {
    if (!token) throw new Error('No token');
    return adminUsersService.resetPassword(token, id, data);
  };

  return {
    admins,
    stats,
    loading,
    error,
    createAdmin,
    updateAdmin,
    deleteAdmin,
    changePassword,
    resetPassword,
    refetch: loadAdmins,
  };
}
